import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import AdminApp from './admin/AdminApp'
import AffiliateApp from './affiliate/AffiliateApp'
import App from './App'
import AuthCallback from './AuthCallback'
import DistributionEmbedPage from './embed/DistributionEmbedPage'
import Header from './Header'
import MagicLinkLogin from './MagicLinkLogin'
import { hasRole, useAuthSession } from './useAuthSession'
import { useRoute } from './useRoute'
import { useTheme } from './useTheme'
import './styles.css'

const rootEl = document.getElementById('root')
if (!rootEl)
  throw new Error('Portal mount point #root not found')

// /distribution is canonical; old `/Distribution`, `/admin`, and `#admin`
// routes redirect for backward compat.

function isDistributionPath(path: string): boolean {
  return path.toLowerCase() === '/distribution'
    || path.toLowerCase().startsWith('/distribution/')
}

function isTradingPath(path: string): boolean {
  return path.toLowerCase() === '/trading'
    || path.toLowerCase().startsWith('/trading/')
}

function isAffiliatePath(path: string): boolean {
  return path.toLowerCase() === '/affiliate'
    || path.toLowerCase().startsWith('/affiliate/')
}

function Router() {
  const route = useRoute()
  const { session, email, role, loading } = useAuthSession()
  const theme = useTheme()

  // Backward-compat redirects: old admin links and case variants bounce to the
  // lowercase canonical /distribution route.
  // Drops after one release once we've confirmed nobody's bookmark or link
  // still points at the old paths.
  useEffect(() => {
    if (window.location.hash === '#admin') {
      console.warn('hash route #admin is deprecated — use /distribution')
      window.location.hash = ''
      route.navigate('/distribution', true)
      return
    }
    if (route.path.toLowerCase() === '/admin') {
      console.warn('/admin is deprecated — use /distribution')
      route.navigate('/distribution', true)
      return
    }
    // Trading briefly shipped as a nested /distribution tab (2026-07-01)
    // before becoming its own parent page — bounce those links.
    if (route.path.toLowerCase().startsWith('/distribution/trading')) {
      route.navigate(route.path.toLowerCase().replace('/distribution/trading', '/trading'), true)
      return
    }

    const canonicalPath = route.path.toLowerCase()

    if ((isDistributionPath(route.path) || isTradingPath(route.path) || isAffiliatePath(route.path)) && route.path !== canonicalPath) {
      route.navigate(canonicalPath, true)
    }
  }, [route])

  useEffect(() => {
    document.body.classList.toggle('is-dark-pref', theme.isDark)
  }, [theme.isDark])

  if (loading) {
    return (
      <div className="brutalist-root">
        <main className="b-main">
          <p className="b-mono-dim">CHECKING SESSION…</p>
        </main>
      </div>
    )
  }

  if (route.path === '/auth/callback') {
    return <AuthCallback route={route} />
  }

  // Public iframe embed: /embed/distribution?sport=<slug>. Renders the
  // carrier grid standalone — no auth, no header, transparent background
  // so the host page's color shows through. Used by partner sites via
  // <iframe src="…/embed/distribution?sport=f1">.
  if (route.path.toLowerCase() === '/embed/distribution') {
    return <DistributionEmbedPage />
  }

  if (!session) {
    if (route.path !== '/login') {
      sessionStorage.setItem('postLoginPath', route.path)
      window.history.replaceState({}, '', '/login')
    }
    return <MagicLinkLogin />
  }

  const headerProps = {
    route,
    email,
    role,
    isDark: theme.isDark,
    onToggleTheme: theme.toggle,
  }

  // /distribution — gated to `user` tier or above. `external` users can
  // sign in but only see the embed page; they get bounced back to / if
  // they navigate here. Read-only by default; SportEditor /
  // SportsbookEditor expose the Edit button per role (sports require
  // admin+, sportsbooks require trading+).
  if (isDistributionPath(route.path)) {
    if (!hasRole(role, 'user')) {
      window.history.replaceState({}, '', '/')
      return (
        <div className={`brutalist-root${theme.isDark ? ' is-dark' : ''}`}>
          <Header {...headerProps} />
          <App isDark={theme.isDark} role={role} />
        </div>
      )
    }
    return (
      <div className={`brutalist-root${theme.isDark ? ' is-dark' : ''}`}>
        <Header {...headerProps} />
        <AdminApp route={route} />
      </div>
    )
  }

  // /trading — its own parent page (like /distribution), gated to
  // `trading` tier or above: the notes carry internal desk detail (Slack
  // channels, league contacts). Lower roles bounce to the embed page.
  if (isTradingPath(route.path)) {
    if (!hasRole(role, 'trading')) {
      window.history.replaceState({}, '', '/')
      return (
        <div className={`brutalist-root${theme.isDark ? ' is-dark' : ''}`}>
          <Header {...headerProps} />
          <App isDark={theme.isDark} role={role} />
        </div>
      )
    }
    return (
      <div className={`brutalist-root${theme.isDark ? ' is-dark' : ''}`}>
        <Header {...headerProps} />
        <AdminApp route={route} mode="trading" />
      </div>
    )
  }

  // /affiliate — gated to `affiliate` tier or above (affiliate + owner).
  // Everyone else bounces to the embed page; the Header hides the nav link
  // so lower roles never see the option.
  if (isAffiliatePath(route.path)) {
    if (!hasRole(role, 'affiliate')) {
      window.history.replaceState({}, '', '/')
      return (
        <div className={`brutalist-root${theme.isDark ? ' is-dark' : ''}`}>
          <Header {...headerProps} />
          <App isDark={theme.isDark} role={role} />
        </div>
      )
    }
    return (
      <div className={`brutalist-root${theme.isDark ? ' is-dark' : ''}`}>
        <Header {...headerProps} />
        <AffiliateApp route={route} />
      </div>
    )
  }

  // Default: embed page (any signed-in role).
  return (
    <div className={`brutalist-root${theme.isDark ? ' is-dark' : ''}`}>
      <Header {...headerProps} />
      <App isDark={theme.isDark} role={role} />
    </div>
  )
}

createRoot(rootEl).render(
  <StrictMode>
    <Router />
  </StrictMode>,
)
